import React, { useState, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'

import styles from '../component-styles/campaign-codes-administration.module.scss'
import { FormRow, LabelSlot, FieldSlot } from './row'
import Modal, { ModalHead, ModalBody, ModalFooter } from './modal'
import Heading from './heading'
import Input, { Label } from './input'
import Button from './button'
import { clientInstance } from '../store/sagas'
import { getAAFTPDetailsAsync } from '../store/actions'
import { CampaignCodesState } from '../store/campaign-codes-reducer'
import ConfirmationButtons from './confirmation-buttons'
import { SiteState } from '../store/site-reducer'

interface ManageProps {
  onToggle: () => void
  ftpDetails: FTPDetails
}

export function Manage({
  onToggle,
  ftpDetails,
}: ManageProps): React.ReactElement {
  const dispatch = useDispatch()
  const [buttonIsDisabled, setButtonIsDisabled] = useState(true)
  const [form, setForm] = useState(ftpDetails)

  const updateDetails = async (details: FTPDetails): Promise<void> => {
    const res = await clientInstance.updateAAFTPDetails(details)
    if (res) {
      dispatch(getAAFTPDetailsAsync())
    }
  }

  return (
    <Modal active toggleActive={onToggle} narrow type="white">
      <ModalHead>
        <Heading type={3} align="left" className={styles.noMargin}>
          Edit Adobe FTP account details
        </Heading>
      </ModalHead>
      <ModalBody>
        <FormRow last>
          <LabelSlot column>
            <Label id="adobeFTPHost">Host</Label>
          </LabelSlot>
          <FieldSlot column>
            <Input
              id="adobeFTPHost"
              name="adobeFTPHost"
              value={form.adobeFTPHost}
              autoComplete="off"
              onChange={(event: React.ChangeEvent<HTMLInputElement>): void => {
                const { value: val } = event.target
                setButtonIsDisabled(false)
                setForm({ ...form, ...{ adobeFTPHost: val } })
              }}
            />
          </FieldSlot>
        </FormRow>
        <FormRow last>
          <LabelSlot column>
            <Label id="adobeFTPLogin">Login</Label>
          </LabelSlot>
          <FieldSlot column>
            <Input
              id="adobeFTPLogin"
              name="adobeFTPLogin"
              value={form.adobeFTPLogin}
              autoComplete="off"
              onChange={(event: React.ChangeEvent<HTMLInputElement>): void => {
                const { value: val } = event.target
                setButtonIsDisabled(false)
                setForm({ ...form, ...{ adobeFTPLogin: val } })
              }}
            />
          </FieldSlot>
        </FormRow>
        <FormRow last>
          <LabelSlot column>
            <Label id="adobeFTPPassword">New password</Label>
          </LabelSlot>
          <FieldSlot column>
            <Input
              id="adobeFTPPassword"
              name="adobeFTPPassword"
              type="password"
              value={form.adobeFTPPassword || ''}
              autoComplete="new-password"
              onChange={(event: React.ChangeEvent<HTMLInputElement>): void => {
                const { value: val } = event.target
                setButtonIsDisabled(false)
                setForm({ ...form, ...{ adobeFTPPassword: val } })
              }}
            />
          </FieldSlot>
        </FormRow>
        <FormRow last>
          <LabelSlot column>
            <Label id="relationID">Relation ID</Label>
          </LabelSlot>
          <FieldSlot column>
            <Input
              id="relationID"
              name="relationID"
              value={form.relationID}
              autoComplete="off"
              onChange={(event: React.ChangeEvent<HTMLInputElement>): void => {
                const { value: val } = event.target
                setButtonIsDisabled(false)
                setForm({ ...form, ...{ relationID: val } })
              }}
            />
          </FieldSlot>
        </FormRow>
        <FormRow last>
          <LabelSlot column>
            <Label id="reportID">Report ID</Label>
          </LabelSlot>
          <FieldSlot column>
            <Input
              id="reportID"
              name="reportID"
              value={form.reportID}
              autoComplete="off"
              onChange={(event: React.ChangeEvent<HTMLInputElement>): void => {
                const { value: val } = event.target
                setButtonIsDisabled(false)
                setForm({ ...form, ...{ reportID: val } })
              }}
            />
          </FieldSlot>
        </FormRow>
      </ModalBody>
      <ModalFooter>
        <ConfirmationButtons
          disabled={buttonIsDisabled}
          type="simple"
          className={styles.confirmationButtons}
          onYes={(): void => {
            updateDetails(form)
            onToggle()
          }}
          onNo={(): void => {
            onToggle()
          }}
          yesLabel="Save"
          noLabel="Go back"
        />
      </ModalFooter>
    </Modal>
  )
}

export default function AdobeAdmin(): React.ReactElement {
  const dispatch = useDispatch()
  const { site }: { site: SiteState } = useSelector((s: any): any => s)

  const [isAdobe, setIsAdobe] = useState(false)
  const [editable, setEditable] = useState(false)

  const { ftpDetails } = useSelector(
    (s: any): CampaignCodesState => s.campaignCodes,
  )

  useEffect((): void => {
    if (isAdobe) {
      dispatch(getAAFTPDetailsAsync())
    }
    // eslint-disable-next-line
  }, [isAdobe])

  useEffect((): void => {
    const isA = !!(
      site &&
      site.selected &&
      site.selected.dataSource === 'AA_REPORT'
    )
    if (isA !== isAdobe) {
      setIsAdobe(isA)
    }
    // eslint-disable-next-line
  }, [site])

  // return nothing for non-adobe account
  if (!isAdobe) {
    return <></>
  }

  return (
    <>
      <div className={styles.checkboxPanelCollapsed}>
        <div className={styles.row}>
          <h3>Adobe FTP account details</h3>
          <Button
            type="flatPink"
            onClick={(): void => {
              setEditable(true)
            }}
          >
            Edit
          </Button>
        </div>
      </div>
      {editable && (
        <Manage
          onToggle={() => {
            setEditable(false)
          }}
          ftpDetails={ftpDetails}
        />
      )}
    </>
  )
}
